import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { EventAiResponse } from "@/components/events/detail/EventAiResponse";
import { EventDetailHeader } from "@/components/events/detail/EventDetailHeader";
import { EventEmailBody } from "@/components/events/detail/EventEmailBody";
import { EventExtractedFields } from "@/components/events/detail/EventExtractedFields";
import { EventProcessingMetadata } from "@/components/events/detail/EventProcessingMetadata";
import { DeleteEventDialog } from "@/components/events/DeleteEventDialog";
import { EditEventDialog } from "@/components/events/EditEventDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useEventQuery } from "@/hooks/useEventQuery";
import { useMarkEventSeen } from "@/hooks/useMarkEventSeen";
import { ApiError } from "@/lib/api";

interface LocationState {
  from?: string;
}

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const backTo = `/events${state?.from ?? ""}`;

  const query = useEventQuery(id);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Auto-mark as seen when the detail page first loads and the event is unseen.
  // The ref guard prevents double-firing in React Strict Mode.
  const markedSeenRef = useRef<string | null>(null);
  const markSeen = useMarkEventSeen();
  useEffect(() => {
    const event = query.data;
    if (!event) return;
    if (event.seenAt !== null) return;
    if (markedSeenRef.current === event.id) return;
    markedSeenRef.current = event.id;
    markSeen.mutate(event.id);
  }, [query.data, markSeen]);

  return (
    <div className="container max-w-4xl space-y-4 py-4 md:space-y-6 md:py-8">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to={backTo}>
            <ArrowLeft />
            Torna alla lista
          </Link>
        </Button>

        {query.data && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Modifica
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Elimina
            </Button>
          </div>
        )}
      </div>

      {query.isLoading && <EventDetailSkeleton />}

      {query.isError && query.error instanceof ApiError && query.error.status === 404 && (
        <NotFoundCard />
      )}

      {query.isError &&
        !(query.error instanceof ApiError && query.error.status === 404) && (
          <ErrorCard
            message={
              query.error instanceof Error
                ? query.error.message
                : "Errore sconosciuto"
            }
            onRetry={() => query.refetch()}
          />
        )}

      {query.data && (
        <>
          <div className="space-y-6">
            <Card>
              <EventDetailHeader event={query.data} />
            </Card>
            <EventExtractedFields event={query.data} />
            <EventEmailBody event={query.data} />
            <EventAiResponse event={query.data} />
            <EventProcessingMetadata event={query.data} />
          </div>

          <EditEventDialog
            key={`edit-${query.data.updatedAt}`}
            event={query.data}
            open={editOpen}
            onOpenChange={setEditOpen}
          />

          <DeleteEventDialog
            event={query.data}
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
          />
        </>
      )}
    </div>
  );
}

function EventDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="space-y-4 p-6">
          <Skeleton className="h-7 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="space-y-3 p-6">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-2/3" />
        </CardContent>
      </Card>
      <Card>
        <CardContent className="space-y-3 p-6">
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

function NotFoundCard() {
  return (
    <Card>
      <CardContent className="space-y-4 p-8 text-center">
        <h2 className="text-lg font-semibold">Evento non trovato</h2>
        <p className="text-sm text-muted-foreground">
          L'evento richiesto non esiste o e' stato rimosso.
        </p>
        <Button asChild variant="outline" size="sm">
          <Link to="/events">Torna alla lista</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

interface ErrorCardProps {
  message: string;
  onRetry: () => void;
}

function ErrorCard({ message, onRetry }: ErrorCardProps) {
  return (
    <Card>
      <CardContent className="space-y-4 p-8 text-center">
        <h2 className="text-lg font-semibold">Errore caricamento evento</h2>
        <p className="text-sm text-muted-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Riprova
        </Button>
      </CardContent>
    </Card>
  );
}
